// =====================================================================
// error-tracker — POSTs unhandled errors + promise rejections to the
// in-cluster errors service. Respects the cookie-consent analytics opt-in:
// without consent we drop the event entirely (no PII, no message).
// Throttled to 1 event per signature per minute to keep noise low.
// =====================================================================

const ENDPOINT = '/api/v1/errors/track';
const CONSENT_KEY = 'aniphys_cookie_consent';
const SERVICE = 'client';
const THROTTLE_MS = 60_000;

const lastSentAt = new Map<string, number>();

function analyticsAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    return parsed?.analytics === true;
  } catch {
    return false;
  }
}

function signature(message: string, stack: string | undefined): string {
  const head = stack?.split('\n').slice(0, 3).join('\n') ?? message;
  let h = 0;
  for (let i = 0; i < head.length; i += 1) {
    h = (h * 31 + head.charCodeAt(i)) | 0;
  }
  return `${h}`;
}

function throttled(sig: string): boolean {
  const now = Date.now();
  const prev = lastSentAt.get(sig) ?? 0;
  if (now - prev < THROTTLE_MS) return true;
  lastSentAt.set(sig, now);
  return false;
}

export function reportError(
  err: unknown,
  info?: { componentStack?: string | null },
  context?: { requestId?: string },
): void {
  if (!analyticsAllowed()) return;

  let message: string;
  let stack: string | undefined;
  if (err instanceof Error) {
    message = err.message || 'Error';
    stack = err.stack;
  } else if (typeof err === 'string') {
    message = err;
  } else {
    try {
      message = JSON.stringify(err);
    } catch {
      message = 'Unknown error';
    }
  }

  const sig = signature(message, stack);
  if (throttled(sig)) return;

  const body = {
    service: SERVICE,
    level: 'error',
    message: message.slice(0, 2000),
    stack: (stack ?? info?.componentStack ?? undefined)?.slice(0, 20_000),
    meta: {
      url: typeof location !== 'undefined' ? location.pathname : undefined,
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      request_id: context?.requestId,
    },
  };

  try {
    const payload = JSON.stringify(body);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // swallow — never let the tracker itself crash the app
  }
}

let installed = false;

export function installErrorTracker(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason);
  });
}
